import * as React from 'react';
import { ContainerContext, mapProps } from './index';
import {
    AddUserTitleDialog,
    WarningDialog,
    BaseSettingsContainer,
    EditUserTitleDialog,
    Table,
    BaseElement,
    HeaderButton,
} from '../components';
import { connect } from 'react-redux';
import actions, { ActionRequest } from '../actions';
import { Avatar } from '@mui/material';
import { UserTitleRole } from '../enums';
import {
    AddOutlined,
    DeleteOutlineOutlined,
    ModeEditOutlineOutlined,
    SettingsOutlined as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { StateMap } from '../reducers';
import useMediaQuery from '@mui/material/useMediaQuery';

interface Props extends ContainerContext.Props {
}

type DialogType = 'Add' | 'Edit' | 'Delete';
interface State {
    readonly openDialog: DialogType | null;
    readonly selectedUser: Entities.UserTitleRoleData | null;
}

class Container extends BaseElement<Props, State> {
    state: State = {
        openDialog: null,
        selectedUser: null,
    };

    protected renderContainer(): React.JSX.Element {
        const buttons: HeaderButton[] | undefined = this.props.userTitleRole === UserTitleRole.Viewer ? undefined : [
            { text: 'Add User', icon: AddOutlined, onClick: this.openAddUserDialog},
        ];
        return (
            <BaseSettingsContainer
                {...this.props}
                styled={this.props.styled}
                title = "Project settings"
                TitleIcon = {SettingsIcon}
                buttons = {buttons}
            >
                {this.renderContent()}
            </BaseSettingsContainer>
        );
    }

    protected renderDialogs(): React.JSX.Element {
        return (
            <>
                {this.renderAddUserDialog()}
                {this.renderEditUserDialog()}
                {this.renderDeleteUserDialog()}
            </>
        );
    }

    private renderContent = () => {
        const { userTitleRole } = this.props;
        if (userTitleRole < UserTitleRole.Owner) {
            return <></>;
        }

        const columns: any = [
            { title: 'User', field: 'username', defaultGroupSort: 'asc' },
            { title: 'Role', field: 'role' },
        ];
        return (
            <Table
                styled={this.props.styled}
                data={this.getData}
                columns={columns}
                options={{
                    showTitle: false,
                    selection: false,
                    paging: true,
                    pageSize: 10,
                    emptyRowsWhenPaging: false,
                    search: true,
                    sorting: true,
                    draggable: false,
                    actionsColumnIndex: -1
                }}
                actions={
                    [
                        rowData => ({
                            icon: ModeEditOutlineOutlined,
                            tooltip: 'Edit',
                            iconProps: {
                                color: 'primary',
                            },
                            onClick: () => this.openEditUserDialog(rowData.user),
                        }),
                        rowData => ({
                            icon: DeleteOutlineOutlined,
                            tooltip: 'Delete',
                            iconProps: {
                                color: 'primary',
                            },
                            onClick: () => this.openDeleteUserDialog(rowData.user),
                        }),
                    ]
                }
            />
        );
    }

    private getData = async (query: any) => {
        const { titleId } = this.props;
        const { search, page, pageSize, orderBy, orderDirection } = query;
        const result = await ActionRequest.get(`title/${titleId}/listUsers`, {
            search,
            page: page + 1,
            perPage: pageSize,
            orderBy: orderBy ? orderBy.field : null,
            orderDirection: orderDirection !== "" ? orderDirection : null,
        }, null, false);
        if (!result) {
            return {
                data: [],
                page: 0,
                totalCount: 0,
            };
        }

        const data = result.entities.map((user: Entities.UserTitleRoleData) => ({
            user,
            username: this.getUser(user),
            role: UserTitleRole[user.userTitleRole].toString(),
        }));

        return {
            data,
            page: result.page - 1,
            totalCount: result.totalCount,
        };
    };

    private getUser = (user: Entities.UserTitleRoleData) => {
        const { username, name, picture } = user;

        return (
            <div style={{ display: 'flex' }}>
                <Avatar
                    alt="Remy Sharp"
                    src={picture}
                    sx={{
                        display: { sm: 'none' },
                        height: 40,
                        width: 40,
                    }}
                />
                <div style={{ display: 'inline-block', margin: '0px 8px' }}>
                    {name !== '' && (
                        <>
                            <div style={{ fontSize: 14 }} >
                                {name}
                            </div>
                            <div style={{ fontSize: 12 }} >
                                {username}
                            </div>
                        </>
                    )}
                    {name === '' && (
                        <div style={{ fontSize: 14 }} >
                            {username}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    private openAddUserDialog = () => this.setState({ openDialog: 'Add' });
    private renderAddUserDialog = () => {
        const { openDialog } = this.state;
        return (
            <AddUserTitleDialog
                styled={this.props.styled}
                open={openDialog === 'Add'}
                title={'Add User'}
                onClose={this.closeDialog}
                onCreate={this.onCreate}
            />
        );
    };

    private openDeleteUserDialog = (selectedUser: Entities.UserTitleRoleData) => this.setState({ openDialog: 'Delete', selectedUser });
    private renderDeleteUserDialog = () => {
        const { openDialog, selectedUser } = this.state;
        if (!selectedUser) {
            return;
        }
        return (
            <WarningDialog
                styled={this.props.styled}
                open={openDialog === 'Delete'}
                title="Remove User"
                content={`This will permanently delete the user ${selectedUser.username} from the title.`}
                onClose={this.closeDialog}
                onSubmit={this.onDeleteUser}
                maxWidth={'xs'}
            />
        );
    };
    private onDeleteUser = async () => {
        const { titleId } = this.props;
        const { selectedUser } = this.state;
        this.closeDialog();
        if (!selectedUser) {
            return;
        }
        await actions.title.deleteTitleUser(titleId, selectedUser.username);
    };

    private openEditUserDialog = (selectedUser: Entities.UserTitleRoleData) => this.setState({ openDialog: 'Edit', selectedUser });
    private renderEditUserDialog = () => {
        const { openDialog, selectedUser } = this.state;
        if (!selectedUser) {
            return;
        }
        return (
            <EditUserTitleDialog
                styled={this.props.styled}
                open={openDialog === 'Edit'}
                title={'Edit User Dialog'}
                selectedUser={selectedUser}
                onClose={this.closeDialog}
                onUpdate={this.onEditUser}
            />
        );
    };
    private onEditUser = async (role: UserTitleRole) => {
        const { titleId } = this.props;
        const { selectedUser } = this.state;
        if (!selectedUser) {
            return;
        }
        const { username } = selectedUser;
        await actions.title.editTitleUser(titleId, username, role);
    };

    private closeDialog = () => this.setState({ openDialog: null, selectedUser: null });

    private onCreate = async (username: string, role: UserTitleRole) => {
        this.closeDialog();
        const { titleId } = this.props;
        await actions.title.addTitleUser(titleId, username, role);
    };
}

const mapStateToProps = (state: StateMap): Props => ({
    ...mapProps(state),
});

const AppContainer = (props: Props) =>
{
    const smallUp = useMediaQuery((theme) => theme.breakpoints.up('sm'));
    const navigate = useNavigate();
    return (<Container {...props} styled={{themeMode: props.app.themeMode, smallUp}} navigate={navigate} />);
};
export default connect(mapStateToProps)(AppContainer);
